import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserTasks, createTask, deleteTask, updateTask, getAllUsers } from '../services/api';
import { useAdmin } from '../hooks/useAdmin';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const formatRetellAgentLabel = (agent) => {
  const rawName = agent?.agent_name || agent?.agent_id || '';
  return rawName.replace(/varification/gi, 'verification');
};

const DEFAULT_AGENT_NAME = '';
const SCRIPT_PLACEHOLDER = [
  '- Caller company details and background',
  '- Customer company details and context',
  '- Purpose of the call and expected outcome',
  '- Product/service details to mention',
  '- Asset details to reference in conversation',
  '- Objection handling notes (optional)'
].join('\n');

function TaskManagement() {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [callingBy, setCallingBy] = useState('');
  const [agentName, setAgentName] = useState(DEFAULT_AGENT_NAME);
  const [callerCompany, setCallerCompany] = useState('The Tech Marketer');
  const [assets, setAssets] = useState('Signal-First Demand Generation content from Market Wavegen');
  const [purposeOfCall, setPurposeOfCall] = useState('Discovery Call');
  const [showCustomPurpose, setShowCustomPurpose] = useState(false);
  const [customPurpose, setCustomPurpose] = useState('');
  const [retellFromNumber, setRetellFromNumber] = useState('');
  const [script, setScript] = useState('');
  const [creating, setCreating] = useState(false);

  // Retell agents/phones lists
  const [retellAgents, setRetellAgents] = useState([]);
  const [retellPhoneNumbers, setRetellPhoneNumbers] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);

  // Edit task state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [editTaskDescription, setEditTaskDescription] = useState('');
  const [editCallingBy, setEditCallingBy] = useState('');
  const [editAgentName, setEditAgentName] = useState(DEFAULT_AGENT_NAME);
  const [editCallerCompany, setEditCallerCompany] = useState('The Tech Marketer');
  const [editAssets, setEditAssets] = useState('Signal-First Demand Generation content from Market Wavegen');
  const [editPurposeOfCall, setEditPurposeOfCall] = useState('Discovery Call');
  const [showEditCustomPurpose, setShowEditCustomPurpose] = useState(false);
  const [editCustomPurpose, setEditCustomPurpose] = useState('');
  const [editRetellFromNumber, setEditRetellFromNumber] = useState('');
  const [editScript, setEditScript] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isAdmin) loadUsers();
    loadRetellAgents();
    loadRetellPhoneNumbers();
  }, [isAdmin]);

  const refreshRetellData = async () => {
    await Promise.all([loadRetellAgents(), loadRetellPhoneNumbers()]);
  };

  useEffect(() => {
    loadTasks();
  }, [selectedUserId]);

  const loadUsers = async () => {
    try {
      const response = await getAllUsers(1, 200);
      if (response.success && response.data) setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadRetellAgents = async () => {
    setLoadingAgents(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/retell/agents`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success && Array.isArray(data.agents)) {
        // Keep latest record for each unique agent_id
        const sortedAgents = [...data.agents].sort(
          (a, b) => Number(b.last_modification_timestamp || 0) - Number(a.last_modification_timestamp || 0)
        );

        const uniqueAgentsMap = new Map();
        sortedAgents.forEach((agent) => {
          const id = agent.agent_id || '';
          if (id && !uniqueAgentsMap.has(id)) {
            uniqueAgentsMap.set(id, agent);
          }
        });

        setRetellAgents(Array.from(uniqueAgentsMap.values()));
      }
    } catch (e) {
      console.error('Could not load Retell agents:', e);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadRetellPhoneNumbers = async () => {
    setLoadingPhoneNumbers(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/retell/phone-numbers`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success && Array.isArray(data.phoneNumbers)) setRetellPhoneNumbers(data.phoneNumbers);
    } catch (e) {
      console.error('Could not load Retell phone numbers:', e);
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await getUserTasks(selectedUserId);
      if (response.success) setTasks(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim()) { setError('Task name is required'); return; }
    if (!agentName.trim()) { setError('Agent name is required'); return; }
    setCreating(true);
    setError(null);
    try {
      const finalPurpose = purposeOfCall === 'Custom' ? customPurpose : purposeOfCall;
      const response = await createTask({
        task_name: newTaskName.trim(),
        description: newTaskDescription ? newTaskDescription.trim() : null,
        calling_by: callingBy,
        agent_name: agentName,
        caller_company: callerCompany,
        assets,
        purpose_of_call: finalPurpose,
        retell_from_number: retellFromNumber || null,
        script: script || null,
      });
      if (response.success) {
        setShowCreateModal(false);
        setNewTaskName('');
        setNewTaskDescription('');
        setCallingBy('');
        setAgentName(DEFAULT_AGENT_NAME);
        setCallerCompany('The Tech Marketer');
        setAssets('Signal-First Demand Generation content from Market Wavegen');
        setPurposeOfCall('Discovery Call');
        setShowCustomPurpose(false);
        setCustomPurpose('');
        setRetellFromNumber('');
        setScript('');
        setError(null);
        loadTasks();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTask = async (taskId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this task? All records will be deleted.')) return;
    try {
      const response = await deleteTask(taskId);
      if (response.success) loadTasks();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setEditTaskName(task.task_name || '');
    setEditTaskDescription(task.description || '');
    setEditCallingBy(task.calling_by || '');
    setEditAgentName(task.agent_name || DEFAULT_AGENT_NAME);
    setEditCallerCompany(task.caller_company || 'The Tech Marketer');
    setEditAssets(task.assets || 'Signal-First Demand Generation content from Market Wavegen');
    const storedPurpose = task.purpose_of_call || 'Discovery Call';
    const standardPurposes = ['Discovery Call', 'Qualification Call', 'Follow-up Call', 'Sales Pitch', 'Appointment Setting', 'Customer Support', 'Feedback Collection'];
    if (standardPurposes.includes(storedPurpose)) {
      setEditPurposeOfCall(storedPurpose);
      setShowEditCustomPurpose(false);
      setEditCustomPurpose('');
    } else {
      setEditPurposeOfCall('Custom');
      setShowEditCustomPurpose(true);
      setEditCustomPurpose(storedPurpose);
    }
    setEditRetellFromNumber(task.retell_from_number || '');
    setEditScript(task.script || '');
    setShowEditModal(true);
    setError(null);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!editTaskName.trim()) { setError('Task name is required'); return; }
    setUpdating(true);
    setError(null);
    try {
      const finalPurpose = editPurposeOfCall === 'Custom' ? editCustomPurpose : editPurposeOfCall;
      const updateData = {
        task_name: editTaskName.trim(),
        description: editTaskDescription ? editTaskDescription.trim() : null,
        calling_by: editCallingBy,
        agent_name: editAgentName,
        caller_company: editCallerCompany,
        assets: editAssets,
        purpose_of_call: finalPurpose,
        retell_from_number: editRetellFromNumber || null,
        script: editScript || null,
      };
      const response = await updateTask(editingTask.id, updateData);
      if (response.success) {
        setShowEditModal(false);
        setEditingTask(null);
        await loadTasks();
      } else {
        setError(response.message || 'Failed to update task');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update task');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-12 w-12 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-400">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        {/* Admin: User selector */}
        {isAdmin && users.length > 0 && (
          <div className="mb-6 bg-[#1a1f3a]/80 border border-purple-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-purple-400">👑 Admin - View tasks for:</span>
              <select
                value={selectedUserId ?? ''}
                onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
                className="px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 min-w-[200px]"
              >
                <option value="">My Tasks</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username || u.email} {u.role === 'admin' ? '(Admin)' : ''}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                {selectedUserId ? `Showing ${users.find(u => u.id === parseInt(selectedUserId))?.username || 'user'}'s tasks` : 'Showing your tasks'}
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8 flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-white mb-2">Task Management</h1>
            <p className="text-sm text-gray-400">
              {selectedUserId && isAdmin
                ? `Viewing tasks for ${users.find(u => u.id === selectedUserId)?.username || 'user'}`
                : 'Create and manage your lead qualification tasks'}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={!!selectedUserId}
            title={selectedUserId ? 'Create task only for your own account' : ''}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Task
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
            <div className="flex justify-between items-center">
              <p className="text-red-400 text-sm">{error}</p>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 text-xl leading-none">×</button>
            </div>
          </div>
        )}

        {/* Tasks Grid */}
        {tasks.length === 0 ? (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-12 text-center backdrop-blur-sm">
            <div className="text-6xl mb-4">📝</div>
            <p className="text-lg font-medium text-white mb-2">No tasks found</p>
            <p className="text-sm text-gray-400 mb-6">Create your first task to get started with lead qualification</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30"
            >
              Create Your First Task
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm hover:border-blue-500/40 transition-all hover:shadow-lg hover:shadow-blue-600/10"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-white flex-1 mr-3 line-clamp-2">{task.task_name}</h3>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      task.status === 'active'
                        ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                        : task.status === 'completed'
                        ? 'bg-gray-700/50 text-gray-300 border border-gray-600/30'
                        : 'bg-red-900/20 text-red-400 border border-red-500/30'
                    }`}>
                      {task.status}
                    </span>
                    <button
                      onClick={(e) => handleDeleteTask(task.id, e)}
                      className="text-gray-500 hover:text-red-400 transition-colors p-1 rounded-full hover:bg-red-400/10"
                      title="Delete task"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                {task.description && (
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">{task.description}</p>
                )}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700/50">
                  <span className="text-sm text-gray-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {task.record_count || 0} records
                  </span>
                  <span className="text-xs text-gray-500">{formatDate(task.created_at)}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => handleEditTask(task)}
                    className="px-4 py-2 text-sm font-medium text-yellow-400 hover:text-yellow-300 border border-yellow-500/30 hover:border-yellow-500/50 rounded-lg transition-all"
                    title="Edit Task"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── Create Task Modal ─── */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-4xl shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-semibold text-white">Create New Task</h2>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateTask}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Task Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTaskName}
                    onChange={(e) => setNewTaskName(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., Lead Generation Q1 2024"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description (Optional)</label>
                  <textarea
                    value={newTaskDescription}
                    onChange={(e) => setNewTaskDescription(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
                    rows="3"
                    placeholder="Describe what this task is for..."
                  />
                </div>

                {/* Call Configuration */}
                <div className="mb-4 border-t border-gray-700/50 pt-4">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Call Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Select Retell Agent */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Select Retell Agent <span className="text-gray-500">([Calling_By])</span>
                      </label>
                      {loadingAgents ? (
                        <div className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-gray-400 text-sm">Loading agents...</div>
                      ) : (
                        <select
                          value={callingBy}
                          onChange={(e) => {
                            setCallingBy(e.target.value);
                          }}
                          className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                        >
                          <option value="">— Select an agent —</option>
                          {retellAgents.length > 0 ? (
                            retellAgents.map((agent) => (
                              <option key={agent.agent_id} value={agent.agent_id}>
                                {formatRetellAgentLabel(agent)}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No agents found from Retell</option>
                          )}
                        </select>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={refreshRetellData}
                          disabled={loadingAgents || loadingPhoneNumbers}
                          className="px-2 py-1 text-xs font-medium text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded hover:bg-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingAgents || loadingPhoneNumbers ? 'Refreshing...' : 'Refresh Retell Data'}
                        </button>
                        <p className="text-xs text-gray-400">Sync agents and phone numbers from Retell.</p>
                      </div>
                    </div>

                    {/* Agent Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Agent Name <span className="text-gray-500">{"{{agent_name}}"}</span>
                      </label>
                      <input
                        type="text"
                        value={agentName}
                        onChange={(e) => setAgentName(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="Sarah"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-400">The name the agent will use (e.g., "Sarah").</p>
                    </div>

                    {/* Outbound Phone Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Outbound Phone Number</label>
                      {loadingPhoneNumbers ? (
                        <div className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-gray-400 text-sm">Loading phone numbers...</div>
                      ) : (
                        <select
                          value={retellFromNumber}
                          onChange={(e) => setRetellFromNumber(e.target.value)}
                          className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                        >
                          <option value="">Default (Global Settings)</option>
                          {retellPhoneNumbers.length > 0 && retellPhoneNumbers.map((num) => (
                            <option key={num.phone_number} value={num.phone_number}>
                              {num.nickname ? `${num.nickname} (${num.phone_number})` : num.phone_number}
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="mt-1 text-xs text-gray-400">Select the caller ID used for outgoing calls.</p>
                    </div>

                    {/* Caller Company */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Caller Company <span className="text-gray-500">([Caller_Company])</span>
                      </label>
                      <input
                        type="text"
                        value={callerCompany}
                        onChange={(e) => setCallerCompany(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="The Tech Marketer"
                      />
                      <p className="mt-1 text-xs text-gray-400">Used in: "Hello — this is [Calling_By] calling from The [Caller_Company]."</p>
                    </div>

                    {/* Assets */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Assets <span className="text-gray-500">([assets])</span>
                      </label>
                      <input
                        type="text"
                        value={assets}
                        onChange={(e) => setAssets(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="Signal-First Demand Generation content from Market Wavegen"
                      />
                      <p className="mt-1 text-xs text-gray-400">Used in: "Just to confirm — did you interact with the [assets]?"</p>
                    </div>

                    {/* Purpose of Call */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Purpose of Call <span className="text-gray-500">{"([purpose_of_call])"}</span>
                      </label>
                      <select
                        value={purposeOfCall}
                        onChange={(e) => {
                          setPurposeOfCall(e.target.value);
                          setShowCustomPurpose(e.target.value === 'Custom');
                        }}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 mb-2"
                      >
                        <option value="Discovery Call">Discovery Call</option>
                        <option value="Qualification Call">Qualification Call</option>
                        <option value="Follow-up Call">Follow-up Call</option>
                        <option value="Sales Pitch">Sales Pitch</option>
                        <option value="Appointment Setting">Appointment Setting</option>
                        <option value="Customer Support">Customer Support</option>
                        <option value="Feedback Collection">Feedback Collection</option>
                        <option value="Custom">Custom...</option>
                      </select>
                      {showCustomPurpose && (
                        <input
                          type="text"
                          value={customPurpose}
                          onChange={(e) => setCustomPurpose(e.target.value)}
                          className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                          placeholder="Enter custom purpose..."
                          required={showCustomPurpose}
                        />
                      )}
                      <p className="mt-1 text-xs text-gray-400">Select the goal of the agent for this task.</p>
                    </div>

                    {/* Script — full width */}
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Script <span className="text-gray-500">{"{{script}}"}</span>
                      </label>
                      <textarea
                        value={script}
                        onChange={(e) => setScript(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 min-h-[100px]"
                        placeholder={SCRIPT_PLACEHOLDER}
                      />
                      <p className="mt-1 text-xs text-gray-400">The dynamic script variable sent to Retell AI as {"{{script}}"}.</p>
                    </div>

                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewTaskName('');
                      setNewTaskDescription('');
                      setCallingBy('');
                      setAgentName(DEFAULT_AGENT_NAME);
                      setCallerCompany('The Tech Marketer');
                      setAssets('Signal-First Demand Generation content from Market Wavegen');
                      setPurposeOfCall('Discovery Call');
                      setShowCustomPurpose(false);
                      setCustomPurpose('');
                      setRetellFromNumber('');
                      setScript('');
                      setError(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creating ? 'Creating...' : 'Create Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── Edit Task Modal ─── */}
        {showEditModal && editingTask && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-4xl shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-semibold text-white">Edit Task</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTask(null);
                    setError(null);
                  }}
                  className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleUpdateTask}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Task Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={editTaskName}
                    onChange={(e) => setEditTaskName(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., Lead Generation Q1 2024"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description (Optional)</label>
                  <textarea
                    value={editTaskDescription}
                    onChange={(e) => setEditTaskDescription(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
                    rows="3"
                    placeholder="Describe what this task is for..."
                  />
                </div>

                {/* Call Configuration */}
                <div className="mb-4 border-t border-gray-700/50 pt-4">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Call Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Select Retell Agent */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Select Retell Agent <span className="text-gray-500">([Calling_By])</span>
                      </label>
                      {loadingAgents ? (
                        <div className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-gray-400 text-sm">Loading agents...</div>
                      ) : (
                        <select
                          value={editCallingBy}
                          onChange={(e) => {
                            const selectedAgentId = e.target.value;
                            setEditCallingBy(selectedAgentId);
                          }}
                          className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                        >
                          <option value="">— Select an agent —</option>
                          {retellAgents.length > 0 ? (
                            retellAgents.map((agent) => (
                              <option key={agent.agent_id} value={agent.agent_id}>
                                {formatRetellAgentLabel(agent)}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No agents found from Retell</option>
                          )}
                        </select>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={refreshRetellData}
                          disabled={loadingAgents || loadingPhoneNumbers}
                          className="px-2 py-1 text-xs font-medium text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded hover:bg-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingAgents || loadingPhoneNumbers ? 'Refreshing...' : 'Refresh Retell Data'}
                        </button>
                        <p className="text-xs text-gray-400">Sync agents and phone numbers from Retell.</p>
                      </div>
                    </div>

                    {/* Agent Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Agent Name <span className="text-gray-500">{"{{agent_name}}"}</span>
                      </label>
                      <input
                        type="text"
                        value={editAgentName}
                        onChange={(e) => setEditAgentName(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="Sarah"
                      />
                      <p className="mt-1 text-xs text-gray-400">The name the agent will use (e.g., "Sarah").</p>
                    </div>

                    {/* Outbound Phone Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Outbound Phone Number</label>
                      {loadingPhoneNumbers ? (
                        <div className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-gray-400 text-sm">Loading phone numbers...</div>
                      ) : (
                        <select
                          value={editRetellFromNumber}
                          onChange={(e) => setEditRetellFromNumber(e.target.value)}
                          className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                        >
                          <option value="">— Default (Global Settings) —</option>
                          {retellPhoneNumbers.length > 0 && retellPhoneNumbers.map((num) => (
                            <option key={num.phone_number} value={num.phone_number}>
                              {num.nickname ? `${num.nickname} (${num.phone_number})` : num.phone_number}
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="mt-1 text-xs text-gray-400">Select the caller ID used for outgoing calls.</p>
                    </div>

                    {/* Caller Company */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Caller Company <span className="text-gray-500">([Caller_Company])</span>
                      </label>
                      <input
                        type="text"
                        value={editCallerCompany}
                        onChange={(e) => setEditCallerCompany(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="The Tech Marketer"
                      />
                      <p className="mt-1 text-xs text-gray-400">Used in: "Hello — this is [Calling_By] calling from The [Caller_Company]."</p>
                    </div>

                    {/* Assets */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Assets <span className="text-gray-500">([assets])</span>
                      </label>
                      <input
                        type="text"
                        value={editAssets}
                        onChange={(e) => setEditAssets(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        placeholder="Signal-First Demand Generation content from Market Wavegen"
                      />
                      <p className="mt-1 text-xs text-gray-400">Used in: "Just to confirm — did you interact with the [assets]?"</p>
                    </div>

                    {/* Purpose of Call */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Purpose of Call <span className="text-gray-500">{"([purpose_of_call])"}</span>
                      </label>
                      <select
                        value={editPurposeOfCall}
                        onChange={(e) => {
                          setEditPurposeOfCall(e.target.value);
                          setShowEditCustomPurpose(e.target.value === 'Custom');
                        }}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 mb-2"
                      >
                        <option value="Discovery Call">Discovery Call</option>
                        <option value="Qualification Call">Qualification Call</option>
                        <option value="Follow-up Call">Follow-up Call</option>
                        <option value="Sales Pitch">Sales Pitch</option>
                        <option value="Appointment Setting">Appointment Setting</option>
                        <option value="Customer Support">Customer Support</option>
                        <option value="Feedback Collection">Feedback Collection</option>
                        <option value="Custom">Custom...</option>
                      </select>
                      {showEditCustomPurpose && (
                        <input
                          type="text"
                          value={editCustomPurpose}
                          onChange={(e) => setEditCustomPurpose(e.target.value)}
                          className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                          placeholder="Enter custom purpose..."
                          required={showEditCustomPurpose}
                        />
                      )}
                      <p className="mt-1 text-xs text-gray-400">Select the goal of the agent for this task.</p>
                    </div>

                    {/* Script — full width */}
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Script <span className="text-gray-500">{"{{script}}"}</span>
                      </label>
                      <textarea
                        value={editScript}
                        onChange={(e) => setEditScript(e.target.value)}
                        className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 min-h-[100px]"
                        placeholder={SCRIPT_PLACEHOLDER}
                      />
                      <p className="mt-1 text-xs text-gray-400">The dynamic script variable sent to Retell AI as {"{{script}}"}.</p>
                    </div>

                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTask(null);
                      setError(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updating}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating ? 'Updating...' : 'Update Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TaskManagement;
